import axios from 'axios';
import axiosInterceptor from '../utils/axios/axiosInterceptor';
import { projectRunBasePath } from './projectRunsApi';
import {
  ListReplaysResponse,
  ReplayDetail,
  ReplayReportPayload,
  TriggerReplayRequest,
  TriggerReplayResponse,
} from '../types/queryLogReplay';

const api = () => axiosInterceptor(true);

// All endpoints live under /project/{project_id}/runs/{run_id}/replays and are wired in
// services/processor/serverless.yml (functions `trigger-query-replay`, `get-replays`, `get-replay`).
// `projectRunBasePath` returns null when projectId/runId are not valid; callers must guard for that.

/** POST: kick off a new replay ECS task for this run. */
export async function triggerReplay(
  projectId: number,
  runId: number,
  body: TriggerReplayRequest
): Promise<TriggerReplayResponse> {
  const base = projectRunBasePath(projectId, runId);
  if (!base) {
    throw new Error('No project run selected. Cannot trigger replay.');
  }
  const response = await api().post<TriggerReplayResponse>(
    `${base}/replays`,
    body
  );
  return response.data;
}

/** GET: list every replay record for this (project, run). Newest first. */
export async function listReplays(
  projectId: number,
  runId: number
): Promise<ListReplaysResponse> {
  const base = projectRunBasePath(projectId, runId);
  if (!base) {
    throw new Error('No project run selected. Cannot list replays.');
  }
  const response = await api().get<ListReplaysResponse>(`${base}/replays`);
  return response.data;
}

/** GET: fetch a single replay, including a 15-minute presigned URL to the full report when COMPLETED. */
export async function getReplay(
  projectId: number,
  runId: number,
  replayId: string
): Promise<ReplayDetail> {
  const base = projectRunBasePath(projectId, runId);
  if (!base) {
    throw new Error('No project run selected. Cannot fetch replay.');
  }
  const response = await api().get<ReplayDetail>(
    `${base}/replays/${replayId}`
  );
  return response.data;
}

/** Fetch replay detail and download the query log analysis PDF when available. */
export async function downloadQueryLogReportPdf(
  projectId: number,
  runId: number
): Promise<void> {
  const { replays } = await listReplays(projectId, runId);
  const completed = replays.find((replay) => replay.status === 'COMPLETED');
  if (!completed?.replay_id) {
    throw new Error(
      'Query log report is not available yet. Complete a query log replay first.'
    );
  }

  const detail = await getReplay(projectId, runId, completed.replay_id);
  const pdfUrl = detail.report_pdf_presigned_url;
  if (!pdfUrl) {
    throw new Error(
      'Query log report PDF is not ready yet. Please try again in a moment.'
    );
  }

  const link = document.createElement('a');
  link.href = pdfUrl;
  link.download =
    detail.report_pdf_filename || `query-log-report-${completed.replay_id}.pdf`;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Fetch the full replay-report JSON straight from S3 using the presigned URL returned by
 * `getReplay`. We use a fresh axios instance (not the Cognito-protected interceptor) because:
 *   1. S3 rejects extra Authorization headers when the URL is presigned.
 *   2. The request shouldn't include our base API URL prefix.
 */
export async function fetchReplayReport(
  presignedUrl: string
): Promise<ReplayReportPayload> {
  const response = await axios.create().get<ReplayReportPayload>(presignedUrl, {
    transformRequest: [
      (data, headers) => {
        // Strip the default Content-Type we set globally; S3 presigned GETs don't need it
        // and some bucket policies may reject the extra signed-header surface.
        if (headers) {
          delete (headers as Record<string, unknown>)['Content-Type'];
          delete (headers as Record<string, unknown>)['content-type'];
        }
        return data;
      },
    ],
  });
  return response.data;
}
